import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FromLayout from "../../component/FromLayout";
import { FaArrowRightLong } from "react-icons/fa6";
import {
  useVerifyOTPMutation,
  useForgotPasswordMutation,
} from "../../store/api/authSlice";
import { handleApiError } from "../../utils/dateAndTimeConversion";
import { useTranslation } from "react-i18next";

const OTP = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location?.state?.email;

  /* ================= STATES ================= */
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  /* ================= API ================= */
  const [verifyOTP, { isLoading }] = useVerifyOTPMutation();
  const [resendOTP, { isLoading: resendLoading }] = useForgotPasswordMutation();

  /* ================= EFFECTS ================= */

  // Redirect if email missing (refresh case)
  useEffect(() => {
    if (!email) {
      toast.error(t("session_expired"));
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  /* ================= HANDLERS ================= */

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    setErrors((prev) => ({
      ...prev,
      [index]: !value,
    }));

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);

    if (!pasteData) return;

    const newOtp = pasteData.split("");
    while (newOtp.length < 4) newOtp.push("");

    setOtp(newOtp);
    setErrors({});
    document.getElementById(`otp-${newOtp.length - 1}`)?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    otp.forEach((digit, index) => {
      if (!digit) newErrors[index] = true;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(t("otp_enter_complete"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    try {
      const response = await verifyOTP({
        otp: otp.join(""),
        email,
      }).unwrap();

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(t("otp_verified_success"));
        navigate("/reset-password", { state: { email } });
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  const handleResendOTP = async () => {
    toast.dismiss();
    try {
      const response = await resendOTP({ email }).unwrap();

      if (response.statusCode === 200) {
        toast.success(t("otp_resent_success"));
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", ""]);
        setErrors({});
        document.getElementById("otp-0")?.focus();
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  /* ================= UI ================= */

  return (
    <FromLayout title={t("verify_otp")} width="w-40">
      <div className="text-center mb-4">
        <p className="text-light" style={{ opacity: 0.8 }}>
          {t("enter")} {t("the")} 4 {t("digit")} {t("verification")} {t("code")}{" "}
          {t("sent")} {t("to")} <br />
          <strong>{email}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* OTP Inputs */}
        <div className="d-flex justify-content-center gap-3 mb-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              maxLength="1"
              className="form-control text-center"
              style={{
                width: "55px",
                height: "55px",
                fontSize: "22px",
                fontWeight: "600",
                border: errors[index] ? "2px solid red" : "1px solid #ced4da",
              }}
              value={digit}
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handleOtpPaste}
            />
          ))}
        </div>

        {/* Submit */}
        <div className="mb-4 d-flex gap-3">
          <button
            type="button"
            className="btn btn-amber w-100 rounded-3"
            disabled={resendLoading}
            onClick={handleResendOTP}
          >
            {resendLoading ? (
              <div className="spinner-border spinner-border-sm" />
            ) : (
              `${t("resend")} ${t("otp")}`
            )}
          </button>
          <button
            type="submit"
            className="btn btn-amber w-100 rounded-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="spinner-border spinner-border-sm" />
            ) : (
              `${t("verify")} ${t("otp")}`
            )}
          </button>
        </div>

        {/* Back to Login */}
        <div className="text-center mt-4">
          <Link
            to="/login"
            style={{
              color: "#f0b241",
              textDecoration: "none",
              fontWeight: "500",
            }}
          >
            {t("back")} {t("to")} {t("login")}{" "}
            <FaArrowRightLong className="ms-2" />
          </Link>
        </div>
      </form>
    </FromLayout>
  );
};

export default OTP;
